import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { 
  User, Route, Photo, POI, LineMarker, MapState, PhotoUploadProgress, PhotoFilter,
  Club, ClubMembership, ClubFollow, MembershipRequest, AuthState, RegisterData
} from '../types'

interface AppState {
  // Authentication
  auth: AuthState
  
  // User relationships
  relationships: {
    memberships: ClubMembership[]
    following: ClubFollow[]
    administeredClubs: Club[]
    premiumMemberships: ClubMembership[]
  }
  
  // User's routes
  userRoutes: {
    all: Route[]
    private: Route[]
    submitted: Route[]
    published: Route[]
    premium: Route[]
  }
  
  // Club management (for admins)
  clubManagement: {
    pendingMemberRequests: MembershipRequest[]
    pendingRouteSubmissions: Route[]
    managedClubs: Club[]
  }
  
  // Page-specific data
  pageData: {
    currentClub?: Club
    currentProfileUser?: User
    clubRoutes?: Route[]
    userProfileRoutes?: Route[]
    visibleRoutes?: Route[]
  }

  // Current editing context
  currentRoute: Route | null
  isDraftSaving: boolean
  lastSaved: Date | null

  // Media & Content
  photos: Photo[]
  photoUploads: PhotoUploadProgress[]
  photoFilter: PhotoFilter
  pois: POI[]
  lineMarkers: LineMarker[]

  // Map State
  mapState: MapState
  
  // UI State
  activeTab: string
  sidebarOpen: boolean
  
  // Auth Actions
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => void
  register: (data: RegisterData) => Promise<void>
  setAuthState: (state: Partial<AuthState>) => void
  
  // Club Actions
  joinClub: (clubId: string, message?: string) => Promise<void>
  followClub: (clubId: string) => Promise<void>
  unfollowClub: (clubId: string) => Promise<void>
  createClub: (clubData: Partial<Club>) => Promise<Club>
  
  // Route Submission Actions
  submitRouteToClub: (routeId: string, clubId: string) => Promise<void>
  withdrawRouteSubmission: (routeId: string) => Promise<void>
  approveRoute: (routeId: string) => Promise<void>
  rejectRoute: (routeId: string, reason: string) => Promise<void>
  
  // Route Assignment Actions (for admins)
  assignRouteToClub: (routeId: string, clubId: string, publish: boolean) => Promise<void>
  unassignRouteFromClub: (routeId: string) => Promise<void>
  
  // Route Management Actions
  setCurrentRoute: (route: Route | null) => void
  createRoute: (route: Omit<Route, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Route>
  updateRoute: (id: string, updates: Partial<Route>) => void
  deleteRoute: (id: string) => void
  loadUserRoutes: () => Promise<void>
  loadClubRoutes: (clubId: string) => Promise<void>
  
  // Membership Management
  loadUserMemberships: () => Promise<void>
  setUserMemberships: (memberships: ClubMembership[]) => void
  
  addPhoto: (photo: Photo) => void
  updatePhoto: (id: string, updates: Partial<Photo>) => void
  deletePhoto: (id: string) => void
  addPhotoUpload: (upload: PhotoUploadProgress) => void
  updatePhotoUpload: (photoId: string, updates: Partial<PhotoUploadProgress>) => void
  removePhotoUpload: (photoId: string) => void
  setPhotoFilter: (filter: PhotoFilter) => void
  getFilteredPhotos: () => Photo[]
  
  addPOI: (poi: POI) => void
  updatePOI: (id: string, updates: Partial<POI>) => void
  deletePOI: (id: string) => void
  
  addLineMarker: (marker: LineMarker) => void
  updateLineMarker: (id: string, updates: Partial<LineMarker>) => void
  deleteLineMarker: (id: string) => void
  
  setMapState: (mapState: Partial<MapState>) => void
  setActiveTab: (tab: string) => void
  setSidebarOpen: (open: boolean) => void
  
  // Draft saving
  markDraftSaving: () => void
  markDraftSaved: () => void
}

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // Initial state
      auth: {
        currentUser: null,
        isAuthenticated: false,
        authToken: undefined,
        isLoading: false,
        profileNeedsCompletion: false
      },
      
      relationships: {
        memberships: [],
        following: [],
        administeredClubs: [],
        premiumMemberships: []
      },
      
      userRoutes: {
        all: [],
        private: [],
        submitted: [],
        published: [],
        premium: []
      },
      
      clubManagement: {
        pendingMemberRequests: [],
        pendingRouteSubmissions: [],
        managedClubs: []
      },
      
      pageData: {},
      
      currentRoute: null,
      isDraftSaving: false,
      lastSaved: null,
      
      photos: [],
      photoUploads: [],
      photoFilter: {},
      pois: [],
      lineMarkers: [],
      
      mapState: {
        center: { lat: -42.8821, lng: 147.3272 }, // Tasmania center
        zoom: 9
      },
      
      activeTab: 'route',
      sidebarOpen: true,
      
      // Auth actions
      signIn: async (email, password) => {
        set((state) => ({ auth: { ...state.auth, isLoading: true } }))
        try {
          // TODO: Implement API call
          console.log('Sign in:', email, password)
          // Mock success for now
          const mockUser: User = {
            id: '1',
            email,
            firstName: 'John',
            lastName: 'Doe',
            displayName: 'John Doe',
            username: 'johndoe',
            createdAt: new Date(),
            lastActive: new Date(),
            emailVerified: true,
            stats: {
              totalRoutes: 0,
              publishedRoutes: 0,
              premiumRoutes: 0,
              totalDistance: 0,
              clubMemberships: 1,
              followedClubs: 0
            }
          }
          set((state) => ({
            auth: {
              currentUser: mockUser,
              isAuthenticated: true,
              authToken: 'mock-token',
              isLoading: false,
              profileNeedsCompletion: false
            }
          }))
          
          // Load user's actual memberships after successful sign-in
          await get().loadUserMemberships()
        } catch (error) {
        set((state) => ({ auth: { ...state.auth, isLoading: false } }))
          throw error
        }
      },
      
      signOut: () => {
        set({
          auth: {
            currentUser: null,
            isAuthenticated: false,
            authToken: undefined,
            isLoading: false,
            profileNeedsCompletion: false
          },
          relationships: {
            memberships: [],
            following: [],
            administeredClubs: [],
            premiumMemberships: []
          },
          userRoutes: {
            all: [],
            private: [],
            submitted: [],
            published: [],
            premium: []
          },
          clubManagement: {
            pendingMemberRequests: [],
            pendingRouteSubmissions: [],
            managedClubs: []
          },
          pageData: {}
        })
      },
      
      register: async (data) => {
        set((state) => ({ auth: { ...state.auth, isLoading: true } }))
        try {
          console.log('Register:', data)
          
          // The actual Firebase registration is handled by the web layer's auth service
          // The authInitializer will automatically sync the authenticated state back to this store
          // So we just need to set loading to false and let the auth state listener handle the rest
          
          // Note: The actual registration happens in the RegisterForm component which calls
          // the Firebase signUpWithEmail function directly. This store method is just a placeholder
          // that manages the loading state.
          
          // The flow is:
          // 1. RegisterForm calls this method to set loading state
          // 2. RegisterForm then calls Firebase signUpWithEmail
          // 3. authInitializer.ts listens for auth state changes
          // 4. authInitializer syncs the Firebase user to this store
          
          set((state) => ({ auth: { ...state.auth, isLoading: false } }))
        } catch (error) {
          set((state) => ({ auth: { ...state.auth, isLoading: false } }))
          throw error
        }
      },
      
      setAuthState: (authState) => set((state) => ({
        auth: { ...state.auth, ...authState }
      })),
      
      // Club actions
      joinClub: async (clubId, message) => {
        // TODO: Implement API call
        console.log('Join club:', clubId, message)
      },
      
      followClub: async (clubId) => {
        // TODO: Implement API call
        console.log('Follow club:', clubId)
      },
      
      unfollowClub: async (clubId) => {
        // TODO: Implement API call
        console.log('Unfollow club:', clubId)
      },
      
      createClub: async (clubData) => {
        // TODO: Implement API call
        console.log('Create club:', clubData)
        return {} as Club
      },
      
      // Route submission actions
      submitRouteToClub: async (routeId, clubId) => {
        // TODO: Implement API call
        console.log('Submit route to club:', routeId, clubId)
      },
      
      withdrawRouteSubmission: async (routeId) => {
        // TODO: Implement API call
        console.log('Withdraw route submission:', routeId)
      },
      
      approveRoute: async (routeId) => {
        // TODO: Implement API call
        console.log('Approve route:', routeId)
      },
      
      rejectRoute: async (routeId, reason) => {
        // TODO: Implement API call
        console.log('Reject route:', routeId, reason)
      },
      
      // Route assignment actions (for admins)
      assignRouteToClub: async (routeId, clubId, publish) => {
        const state = get()
        const currentUser = state.auth.currentUser
        
        if (!currentUser) {
          throw new Error('User must be authenticated to assign routes')
        }
        
        // Find the club name from memberships
        const membership = state.relationships.memberships.find(m => m.clubId === clubId)
        const clubName = membership ? 
          (clubId === 'bike-routes-default' ? 'Bike Routes' : 
           clubId === 'test-club-admin' ? 'Test Admin Club' :
           `Club ${clubId}`) :
          'Unknown Club'
        
        // Update the route
        const routeUpdates: Partial<Route> = {
          assignedClub: {
            clubId,
            clubName,
            assignedAt: new Date(),
            assignedBy: currentUser.id,
            isDirectAssignment: true
          },
          status: publish ? 'published' : 'private',
          updatedAt: new Date()
        }
        
        // If not publishing, clear submission data
        if (!publish) {
          routeUpdates.submission = undefined
        }
        
        // Update the route in store
        state.updateRoute(routeId, routeUpdates)
        
        // TODO: Implement API call
        console.log('Assign route to club:', routeId, clubId, publish)
      },
      
      unassignRouteFromClub: async (routeId) => {
        const state = get()
        
        // Update the route to remove club assignment
        const routeUpdates: Partial<Route> = {
          assignedClub: undefined,
          status: 'private',
          submission: undefined,
          updatedAt: new Date()
        }
        
        // Update the route in store
        state.updateRoute(routeId, routeUpdates)
        
        // TODO: Implement API call
        console.log('Unassign route from club:', routeId)
      },
      
      // Route management actions
      setCurrentRoute: (route) => set({ currentRoute: route }),
      
      createRoute: async (routeData) => {
        // TODO: Implement API call
        const newRoute: Route = {
          ...routeData,
          id: Math.random().toString(36).substr(2, 9),
          createdAt: new Date(),
          updatedAt: new Date()
        }
        
        set((state) => ({
          userRoutes: {
            ...state.userRoutes,
            all: [...state.userRoutes.all, newRoute],
            private: newRoute.status === 'private' 
              ? [...state.userRoutes.private, newRoute] 
              : state.userRoutes.private
          }
        }))
        
        return newRoute
      },
      
      updateRoute: (id, updates) => set((state) => {
        const updateRouteInArray = (routes: Route[]) => 
          routes.map(route => route.id === id ? { ...route, ...updates } : route)
        
        return {
          userRoutes: {
            all: updateRouteInArray(state.userRoutes.all),
            private: updateRouteInArray(state.userRoutes.private),
            submitted: updateRouteInArray(state.userRoutes.submitted),
            published: updateRouteInArray(state.userRoutes.published),
            premium: updateRouteInArray(state.userRoutes.premium)
          },
          currentRoute: state.currentRoute?.id === id 
            ? { ...state.currentRoute, ...updates }
            : state.currentRoute
        }
      }),
      
      deleteRoute: (id) => set((state) => {
        const filterRoute = (routes: Route[]) => routes.filter(route => route.id !== id)
        
        return {
          userRoutes: {
            all: filterRoute(state.userRoutes.all),
            private: filterRoute(state.userRoutes.private),
            submitted: filterRoute(state.userRoutes.submitted),
            published: filterRoute(state.userRoutes.published),
            premium: filterRoute(state.userRoutes.premium)
          },
          currentRoute: state.currentRoute?.id === id ? null : state.currentRoute
        }
      }),
      
      loadUserRoutes: async () => {
        // TODO: Implement API call
        console.log('Load user routes')
      },
      
      loadClubRoutes: async (clubId) => {
        // TODO: Implement API call
        console.log('Load club routes:', clubId)
      },
      
      // Load user memberships from Firebase
      loadUserMemberships: async () => {
        const state = get()
        if (!state.auth.currentUser) {
          console.log('No authenticated user, cannot load memberships')
          return
        }
        
        try {
          console.log('Loading memberships from Firebase for user:', state.auth.currentUser.id)
          
          // This will be implemented by the web layer calling this function
          // For now, just indicate that memberships should be loaded externally
          console.log('Store loadUserMemberships called - should be handled by web layer')
          
          // The web layer should call this store function directly after fetching from Firebase
          // This approach avoids circular dependencies between shared and web packages
        } catch (error) {
          console.error('Failed to load user memberships:', error)
        }
      },
      
      // Set user memberships directly (called by web layer after Firebase fetch)
      setUserMemberships: (memberships: ClubMembership[]) => {
        set((state) => ({
          relationships: {
            ...state.relationships,
            memberships
          }
        }))
        console.log('✅ User memberships set in store:', memberships.length, 'memberships')
      },
      
      // Photo actions
      addPhoto: (photo) => set((state) => {
        // Check if photo with this ID already exists
        if (state.photos.some(p => p.id === photo.id)) {
          console.warn(`⚠️ Photo with ID ${photo.id} already exists, skipping duplicate`)
          return state // Return unchanged state
        }
        return { photos: [...state.photos, photo] }
      }),
      updatePhoto: (id, updates) => set((state) => ({
        photos: state.photos.map(photo => 
          photo.id === id ? { ...photo, ...updates } : photo
        )
      })),
      deletePhoto: (id) => set((state) => ({
        photos: state.photos.filter(photo => photo.id !== id)
      })),
      
      // Photo upload actions
      addPhotoUpload: (upload) => set((state) => ({ 
        photoUploads: [...state.photoUploads, upload] 
      })),
      updatePhotoUpload: (photoId, updates) => set((state) => ({
        photoUploads: state.photoUploads.map(upload => 
          upload.photoId === photoId ? { ...upload, ...updates } : upload
        )
      })),
      removePhotoUpload: (photoId) => set((state) => ({
        photoUploads: state.photoUploads.filter(upload => upload.photoId !== photoId)
      })),
      setPhotoFilter: (filter) => set({ photoFilter: filter }),
      getFilteredPhotos: () => {
        const state = get()
        let filteredPhotos = state.photos
        
        if (state.photoFilter.routeId) {
          filteredPhotos = filteredPhotos.filter(photo => photo.routeId === state.photoFilter.routeId)
        }
        
        if (state.photoFilter.hasGPS !== undefined) {
          filteredPhotos = filteredPhotos.filter(photo => photo.isGeotagged === state.photoFilter.hasGPS)
        }
        
        if (state.photoFilter.tags && state.photoFilter.tags.length > 0) {
          filteredPhotos = filteredPhotos.filter(photo => 
            photo.tags?.some(tag => state.photoFilter.tags!.includes(tag))
          )
        }
        
        if (state.photoFilter.dateRange) {
          filteredPhotos = filteredPhotos.filter(photo => {
            if (!photo.takenAt) return false
            return photo.takenAt >= state.photoFilter.dateRange!.start && 
                   photo.takenAt <= state.photoFilter.dateRange!.end
          })
        }
        
        return filteredPhotos
      },
      
      // POI actions
      addPOI: (poi) => set((state) => ({ 
        pois: [...state.pois, poi] 
      })),
      updatePOI: (id, updates) => set((state) => ({
        pois: state.pois.map(poi => 
          poi.id === id ? { ...poi, ...updates } : poi
        )
      })),
      deletePOI: (id) => set((state) => ({
        pois: state.pois.filter(poi => poi.id !== id)
      })),
      
      // Line marker actions
      addLineMarker: (marker) => set((state) => ({ 
        lineMarkers: [...state.lineMarkers, marker] 
      })),
      updateLineMarker: (id, updates) => set((state) => ({
        lineMarkers: state.lineMarkers.map(marker => 
          marker.id === id ? { ...marker, ...updates } : marker
        )
      })),
      deleteLineMarker: (id) => set((state) => ({
        lineMarkers: state.lineMarkers.filter(marker => marker.id !== id)
      })),
      
      // UI actions
      setMapState: (mapState) => set((state) => ({
        mapState: { ...state.mapState, ...mapState }
      })),
      setActiveTab: (tab) => set({ activeTab: tab }),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      
      // Draft saving actions
      markDraftSaving: () => set({ isDraftSaving: true }),
      markDraftSaved: () => set({ 
        isDraftSaving: false, 
        lastSaved: new Date() 
      })
    }),
    {
      name: 'cya-routes-store'
    }
  )
)
